<template>
  <!-- This component renders SEO meta tags in the document head -->
</template>

<script setup lang="ts">
import { computed, onMounted, watchEffect } from 'vue'
import { useRoute } from 'vue-router'
import { useStrapiStore } from '../stores/strapi'

interface Props {
  /**
   * Page-specific SEO data
   */
  pageSeo?: {
    seoTitle?: string | null
    seoDescription?: string | null
    canonicalUrl?: string | null
    noindex?: boolean | null
    nofollow?: boolean | null
    structuredData?: any | null
  } | null
  /**
   * Page title for fallback
   */
  pageTitle?: string | null
  /**
   * Site name for title suffix
   */
  siteName?: string | null
}

const props = withDefaults(defineProps<Props>(), {
  pageSeo: null,
  pageTitle: null,
  siteName: 'Cross Works Ltd'
})

const route = useRoute()
const strapiStore = useStrapiStore()

// Computed properties for SEO data with fallbacks
const title = computed(() => {
  const pageSeoTitle = props.pageSeo?.seoTitle
  const globalSeoTitle = strapiStore.globalSeo?.seoTitle
  const siteName = props.siteName || 'Cross Works'
  
  if (pageSeoTitle) {
    return `${pageSeoTitle} / ${siteName}`
  }
  
  if (props.pageTitle) {
    return `${props.pageTitle} / ${siteName}`
  }
  
  if (globalSeoTitle) {
    return `${globalSeoTitle} / ${siteName}`
  }
  
  return siteName
})

const description = computed(() => {
  const pageSeoDescription = props.pageSeo?.seoDescription
  const globalSeoDescription = strapiStore.globalSeo?.seoDescription
  const siteDescription = strapiStore.siteInfo?.description
  
  return pageSeoDescription || globalSeoDescription || siteDescription || ''
})

const canonicalUrl = computed(() => {
  const pageSeoCanonical = props.pageSeo?.canonicalUrl
  const globalSeoCanonical = strapiStore.globalSeo?.canonicalUrl
  
  if (pageSeoCanonical) return pageSeoCanonical
  if (globalSeoCanonical) return globalSeoCanonical
  
  if (typeof window !== 'undefined') {
    return `${window.location.origin}${route.fullPath}`
  }
  
  return ''
})

const shouldNoIndex = computed(() => {
  return props.pageSeo?.noindex ?? strapiStore.globalSeo?.noindex ?? false
})

const shouldNoFollow = computed(() => {
  return props.pageSeo?.nofollow ?? strapiStore.globalSeo?.nofollow ?? false
})

const structuredData = computed(() => {
  return props.pageSeo?.structuredData || strapiStore.globalSeo?.structuredData || null
})

const robotsContent = computed(() => {
  const directives = []
  
  if (shouldNoIndex.value) directives.push('noindex')
  else directives.push('index')
  
  if (shouldNoFollow.value) directives.push('nofollow')
  else directives.push('follow')
  
  return directives.join(', ')
})

const updateMetaTags = () => {
  if (typeof document === 'undefined') return

  document.title = title.value

  const updateMetaTag = (name: string, content: string, useProperty = false) => {
    if (!content) return
    
    const attribute = useProperty ? 'property' : 'name'
    let element = document.querySelector(`meta[${attribute}="${name}"]`)
    
    if (!element) {
      element = document.createElement('meta')
      element.setAttribute(attribute, name)
      document.head.appendChild(element)
    }
    
    element.setAttribute('content', content)
  }

  const desc = description.value
  if (desc) {
    updateMetaTag('description', desc)
  }

  updateMetaTag('robots', robotsContent.value)

  // Update canonical link
  const canonicalLink = document.querySelector('link[rel="canonical"]') || document.createElement('link')
  if (!canonicalLink.parentNode) {
    canonicalLink.setAttribute('rel', 'canonical')
    document.head.appendChild(canonicalLink)
  }
  const canonical = canonicalUrl.value
  if (canonical) {
    canonicalLink.setAttribute('href', canonical)
  }

  // Update structured data
  const structuredDataValue = structuredData.value
  if (structuredDataValue) {
    let structuredDataScript = document.querySelector('script[type="application/ld+json"]')
    
    if (!structuredDataScript) {
      structuredDataScript = document.createElement('script')
      structuredDataScript.setAttribute('type', 'application/ld+json')
      document.head.appendChild(structuredDataScript)
    }
    
    structuredDataScript.textContent = JSON.stringify(structuredDataValue)
  }
}

// Watch for changes and update meta tags
watchEffect(() => {
  updateMetaTags()
})

// Initialize meta tags on mount
onMounted(() => {
  updateMetaTags()
})
</script> 